import { useTranslation } from 'react-i18next';
import { styled } from 'goober';
import { useLocation } from 'react-router-dom';

import { WithHtmlSavvyTitle } from '@/with-html-savvy-title';
import { Header, ParagraphLarge } from '@/ui/typography/widgets';
import { Flexbox, FlexContainer } from '@/ui/flexbox/flexbox';
import { Button } from '@/ui/button/button';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { requestProjectIllustration } from '@/assets/images';
import RequestSolutionIllustration from '@/assets/svg/request-solution-illustration.svg?react';
import { modalState } from '@/state/modal.state';
import { Modals } from '@/modals';
import { useSetAtom } from 'jotai';

interface RequestProjectUnauthenticatedPageProps {
  organizationName: string;
  logoUrl: string;
  accountId?: string;
  linkId?: string;
}

export const RequestProjectUnauthenticatedPage = ({
  organizationName,
  logoUrl,
  linkId,
}: RequestProjectUnauthenticatedPageProps) => {
  const { t } = useTranslation('request-project');
  const navigateTo = useNavigateTo();
  const setModal = useSetAtom(modalState);
  const location = useLocation();

  const handleSignIn = () => {
    // Use relative URL (path + search) instead of absolute URL
    const currentPath = `${location.pathname}${location.search}`;

    if (currentPath === '/') {
      navigateTo.signIn(undefined, undefined);
    } else {
      const redirectUrl = `redirectTo=${encodeURIComponent(currentPath)}`;
      navigateTo.signIn(undefined, redirectUrl);
    }
  };

  const handleAskToJoin = () => {
    setModal({
      state: 'askToJoinForm',
      linkId,
      organizationName,
    });
  };

  return (
    <WithHtmlSavvyTitle title={t`unauthenticated.title`}>
      <Container>
        <PageContainer>
          <ContentGrid>
            <LeftSection>
              <LeftSectionContent>
                <ContentSection>
                  <FlexContainer direction="column" gap="20px">
                    <HeaderSection>
                      <LogoContainer>
                        <Logo src={logoUrl} alt="Logo" />
                      </LogoContainer>
                      <PageTitle>{t`unauthenticated.title`}</PageTitle>
                      <Subtitle>
                        {t('unauthenticated.subtitle', {
                          organizationName,
                        })}
                      </Subtitle>
                    </HeaderSection>

                    <DescriptionSection>
                      <Description>
                        {t('unauthenticated.description', {
                          organizationName,
                        })}
                      </Description>
                    </DescriptionSection>

                    <ButtonContainer>
                      <Button
                        variant="primary"
                        onClick={handleSignIn}
                        textCenter
                        width={90}
                      >
                        {t`unauthenticated.signIn`}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleAskToJoin}
                        textCenter
                        width={160}
                      >
                        {t`unauthenticated.askToJoin`}
                      </Button>
                    </ButtonContainer>
                  </FlexContainer>
                </ContentSection>

                <FooterText>{t`unauthenticated.registeredUsersOnly`}</FooterText>
              </LeftSectionContent>
            </LeftSection>

            <RightSection>
              <IllustrationContainer>
                <img
                  src={requestProjectIllustration}
                  alt="Request project illustration"
                  style={{ transform: 'scale(1.2)' }}
                />
              </IllustrationContainer>
              <RequestSolutionIllustration />
            </RightSection>
          </ContentGrid>
        </PageContainer>
      </Container>
      <Modals />
    </WithHtmlSavvyTitle>
  );
};

const Container = styled('div')`
  position: relative;
`;

const PageContainer = styled('div')`
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  background-color: ${({ theme }) => theme.colors.gray.c1};
  overflow: hidden;
  padding: 42px 60px 42px 42px;

  @media (max-width: 768px) {
    padding: 24px 32px 24px 24px;
    overflow-y: auto;
  }

  @media (max-width: 480px) {
    padding: 16px 24px 16px 16px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    padding: 16px 32px 16px 24px;
    overflow-y: auto;
  }
`;

const ContentGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 100vh;
  max-height: 100vh;
  padding: 0;
  grid-gap: 40px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    height: auto;
    max-height: none;
    grid-gap: 24px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    grid-template-columns: 1fr;
    height: auto;
    max-height: none;
    grid-gap: 16px;
  }
`;

const LeftSection = styled('div')`
  background-color: ${({ theme }) => theme.colors.gray.c1};
  padding: 32px 20px 16px 60px;
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;

  @media (max-width: 1200px) {
    padding: 32px 24px 16px 24px;
  }

  @media (max-width: 768px) {
    padding: 16px 8px;
    height: auto;
    max-height: none;
    overflow: visible;
  }

  @media (max-width: 480px) {
    padding: 8px 0;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    padding: 8px 16px;
    height: auto;
    max-height: none;
    overflow: visible;
  }
`;

const LeftSectionContent = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 0.9;

  @media (max-width: 768px) {
    flex: 1;
    justify-content: flex-start;
    gap: 24px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    flex: 1;
    justify-content: flex-start;
    gap: 12px;
  }
`;

const ContentSection = styled('div')`
  display: flex;
  flex-direction: column;
  max-width: 720px;
  padding: 20px 0;
  flex: 0 1 auto;
  overflow: visible;

  @media (max-width: 1200px) {
    justify-content: flex-start;
    margin-bottom: 40px;
    padding: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 0;
    max-width: 100%;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    margin-bottom: 0;
    padding: 0;
    max-width: 100%;
  }
`;

const HeaderSection = styled('div')`
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    margin-bottom: 12px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    margin-bottom: 8px;
  }
`;

const LogoContainer = styled('div')`
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 16px;

    svg {
      width: 100px;
      height: auto;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 12px;

    svg {
      width: 80px;
    }
  }

  @media (max-height: 500px) and (orientation: landscape) {
    margin-bottom: 8px;

    svg {
      width: 80px;
      height: auto;
    }
  }
`;

const PageTitle = styled(Header)`
  color: ${({ theme }) => theme.colors.basics.black};
  margin-bottom: 20px;
  font-size: 53px;
  font-weight: 600;
  line-height: 1.25;

  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    margin-bottom: 12px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 24px;
    margin-bottom: 8px;
    line-height: 1.2;
  }
`;

const Subtitle = styled(ParagraphLarge)`
  color: ${({ theme }) => theme.colors.gray.c12};
  line-height: 1.6;
  font-size: 18px;
  white-space: pre-line;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 13px;
    line-height: 1.4;
  }
`;

const DescriptionSection = styled('div')`
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    margin-bottom: 12px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    margin-bottom: 8px;
  }
`;

const Description = styled(ParagraphLarge)`
  color: ${({ theme }) => theme.colors.gray.c12};
  line-height: 1.6;
  font-size: 18px;
  white-space: pre-line;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    font-size: 13px;
    line-height: 1.4;
  }
`;

const ButtonContainer = styled('div')`
  display: flex;
  gap: 16px;
  height: 40px;

  @media (max-width: 480px) {
    flex-direction: column;
    height: auto;
    gap: 12px;

    button {
      max-width: 100% !important;
      width: 100% !important;
    }
  }

  @media (max-height: 500px) and (orientation: landscape) and (min-width: 481px) {
    height: 36px;
    gap: 12px;

    button {
      height: 36px;
    }
  }
`;

const RightSection = styled(Flexbox)`
  background-color: ${({ theme }) => theme.colors.gray.c1};
  align-items: end;
  justify-content: center;
  flex-direction: column;
  text-align: left;

  @media (max-width: 1200px) {
    display: none;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    display: none;
  }
`;

const IllustrationContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  max-width: 500px;
  max-height: 500px;

  img {
    max-width: 100%;
    height: auto;
    max-height: 100%;
    object-fit: contain;
  }
`;

const FooterText = styled('p')`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray.c11};
  margin: 0;
  padding: 24px 0 0 0;
  line-height: 1.4;
  align-self: flex-start;
  flex-shrink: 0;
  opacity: 0.9;
  margin-top: auto;

  @media (max-width: 768px) {
    padding: 20px 0 0 0;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 16px 0 24px 0;
    font-size: 12px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    padding: 12px 0 16px 0;
    font-size: 11px;
    margin-top: 0;
  }
`;

const Logo = styled('img')``;
